import { useEffect, useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { FileText, Activity, ShieldAlert } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  PageHeader,
  TableFilters,
  DataTable,
  Column,
  StatusBadge,
  MetricCard,
  KPIGrid,
} from '@/components/common';
import { auditLogsApi, AuditLog, QueryAuditLogParams } from '@/api/services';
import { getErrorMessage } from '@/api/client';

export default function AuditLogs() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page] = useState(1);
  const [resultFilter, setResultFilter] = useState<string>('');
  const [actionFilter, setActionFilter] = useState<string>('');

  const fetchLogs = useCallback(
    async (params?: QueryAuditLogParams) => {
      try {
        setLoading(true);
        const response = await auditLogsApi.getAll({
          ...params,
          page,
          limit: 20,
        });
        setLogs(response.data);
        setTotal(response.meta.total);
      } catch (error) {
        toast.error(getErrorMessage(error));
      } finally {
        setLoading(false);
      }
    },
    [page]
  );

  useEffect(() => {
    const params: QueryAuditLogParams = {};
    if (resultFilter) params.result = resultFilter as 'ALLOW' | 'DENY';
    if (actionFilter) params.action = actionFilter;
    fetchLogs(params);
  }, [page, resultFilter, actionFilter, fetchLogs]);

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return timestamp;
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch {
      return timestamp;
    }
  };

  const metrics = useMemo(() => {
    const denied = logs.filter((l) => l.result === 'DENY').length;
    const recent = logs.filter((l) => {
      const logDate = new Date(l.timestamp);
      if (isNaN(logDate.getTime())) return false;
      const now = new Date();
      const diffInHours = (now.getTime() - logDate.getTime()) / (1000 * 60 * 60);
      return diffInHours <= 24;
    }).length;

    return { total, denied, recent };
  }, [logs, total]);

  const columns: Column<AuditLog>[] = [
    {
      key: 'timestamp',
      header: 'Time',
      sortable: true,
      render: (log) => (
        <span className="text-muted-foreground font-mono text-sm">
          {formatTimestamp(log.timestamp)}
        </span>
      ),
    },
    {
      key: 'user',
      header: 'User',
      render: (log) => (
        <div>
          <div className="font-medium">{log.user.fullName}</div>
          <div className="text-xs leading-4 text-muted-foreground">{log.user.email}</div>
        </div>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (log) => (
        <Badge
          variant="outline"
          className={
            log.action === 'GET'
              ? 'border-primary-200 text-primary-500'
              : log.action === 'POST'
                ? 'border-success-200 text-success-600'
                : log.action === 'PUT' || log.action === 'PATCH'
                  ? 'border-warning-200 text-warning-600'
                  : 'border-danger-200 text-danger-600'
          }
        >
          {log.action}
        </Badge>
      ),
    },
    {
      key: 'resource',
      header: 'Resource',
      render: (log) => (
        <div className="max-w-xs truncate font-mono text-sm text-muted-foreground">
          {log.resource}
        </div>
      ),
    },
    {
      key: 'accessCode',
      header: 'Permission',
      render: (log) => (
        <Badge variant="secondary" className="font-mono text-xs">
          {log.accessCode}
        </Badge>
      ),
    },
    {
      key: 'result',
      header: 'Result',
      render: (log) => (
        <StatusBadge status={log.result === 'ALLOW' ? 'approved' : 'rejected'} label={log.result} />
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Audit Logs"
          description="A record of every action taken in the system, with the account and permission behind it."
        />
      </motion.div>

      <div>
        <KPIGrid className="mt-6">
          <MetricCard
            label="Total Logs"
            value={metrics.total}
            sentiment="info"
            icon={<FileText className="h-5 w-5" />}
          />
          <MetricCard
            label="Recent Activity (24h)"
            value={metrics.recent}
            sentiment="neutral"
            icon={<Activity className="h-5 w-5" />}
          />
          <MetricCard
            label="Denied Actions"
            value={metrics.denied}
            sentiment={metrics.denied > 0 ? 'danger' : 'success'}
            icon={<ShieldAlert className="h-5 w-5" />}
          />
        </KPIGrid>
      </div>

      <motion.div variants={fadeUp} className="mt-6 flex items-center gap-4">
        <div className="ml-auto text-sm text-muted-foreground">{total} total records</div>
      </motion.div>

      <motion.div variants={fadeUp} className="mt-6">
        <DataTable
          toolbar={
            <TableFilters
              filters={[
                {
                  id: 'result',
                  label: 'Results',
                  value: resultFilter,
                  options: [
                    { value: 'ALLOW', label: 'Allow' },
                    { value: 'DENY', label: 'Deny' },
                  ],
                  onChange: setResultFilter,
                  placeholder: 'Result',
                  width: 'w-[132px]',
                },
                {
                  id: 'action',
                  label: 'Actions',
                  value: actionFilter,
                  options: [
                    { value: 'GET', label: 'GET' },
                    { value: 'POST', label: 'POST' },
                    { value: 'PUT', label: 'PUT' },
                    { value: 'PATCH', label: 'PATCH' },
                    { value: 'DELETE', label: 'DELETE' },
                  ],
                  onChange: setActionFilter,
                  placeholder: 'Action',
                  width: 'w-[132px]',
                },
              ]}
            />
          }
          data={logs}
          columns={columns}
          loading={loading}
          pagination
          exportable
          columnToggle
          searchable
          emptyTitle="No audit logs found"
          emptyDescription="Audit logs will appear here as users interact with the system."
          emptyAction={null}
        />
      </motion.div>
    </motion.main>
  );
}
